/**
 * Admin Organizations Management Module
 *
 * Organization CRUD for the Manage Organizations page.
 */

import { api } from '../api/client.js';
import type { Organization, CreateOrganizationRequest } from '../api/types.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog, notificationDialog } from '../ui/dialogs.js';
import { showAdSyncWarning } from '../user/settings.js';
import { generateNetworkPrefixSelect } from '../ui/forms.js';

let orgsTable: unknown = null;

/**
 * Fill the organizations table
 */
export async function fillOrganizationsTable(): Promise<void> {
  const response = await apiCall(api.listOrganizations(), 'Failed to Load Organizations');
  if (!response) return;

  const tableData = response.organizations.map((org) => {
    const safeName = escapeAttr(org.name);
    return {
      name: escapeHtml(org.name),
      description: escapeHtml(org.description || ''),
      contact_name: escapeHtml(org.contact_name || ''),
      vhost: escapeHtml(org.vhost || ''),
      bridges: (org.bridges || []).map(b => escapeHtml(b)).join(', ') || '<em>none</em>',
      actions: `
        <button class="ui-button" onclick='modifyOrganization("${safeName}")'>Modify</button>
        <button class="ui-button" onclick='deleteOrganization("${safeName}")'>Delete</button>
      `,
    };
  });

  if (!orgsTable) {
    orgsTable = $('#organizations').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'description', title: 'Description' },
        { data: 'contact_name', title: 'Contact' },
        { data: 'vhost', title: 'VHost' },
        { data: 'bridges', title: 'Bridges' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = orgsTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }

  declareLoaded();
}

/**
 * Build the organization form HTML (for modify — basic fields only)
 */
function buildOrgForm(org?: Organization): string {
  const name = org?.name || '';
  const description = org?.description || '';
  const contactName = org?.contact_name || '';
  const vhost = org?.vhost || '';

  return `
    <div class="form-field">
      <label>Name</label>
      <input id="org-name" value="${escapeAttr(name)}" ${org ? 'readonly' : ''} placeholder="e.g. acme">
    </div>
    <div class="form-field">
      <label>Description</label>
      <input id="org-description" value="${escapeAttr(description)}" placeholder="e.g. ACME Corporation">
    </div>
    <div class="form-field">
      <label>Contact Name</label>
      <input id="org-contact" value="${escapeAttr(contactName)}" placeholder="e.g. John Smith">
    </div>
    <div class="form-field">
      <label>VHost</label>
      <input id="org-vhost" value="${escapeAttr(vhost)}" placeholder="e.g. acme.example.com">
    </div>
  `;
}

/**
 * Build the create form with provisioning options (network + initial user)
 */
function buildCreateForm(): string {
  return buildOrgForm() + `
    <hr style="margin: 12px 0;">
    <h4 style="margin: 8px 0;">Network Provisioning</h4>
    <div class="form-field">
      <label>Private Network Size</label>
      ${generateNetworkPrefixSelect('org-network-prefix', true)}
      <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
        Auto-creates a private bridge with NAT and DHCP. A random RFC1918 CIDR is allocated.
      </div>
    </div>
    <hr style="margin: 12px 0;">
    <h4 style="margin: 8px 0;">Initial User <span style="font-weight:normal;color:var(--color-text-muted);">(optional)</span></h4>
    <div class="form-field">
      <label>Username</label>
      <input id="org-user-username" placeholder="e.g. jsmith">
    </div>
    <div class="form-field">
      <label>Email</label>
      <input id="org-user-email" type="email" placeholder="e.g. jsmith@acme.com">
    </div>
    <div class="form-field">
      <label>Password</label>
      <input id="org-user-password" type="password" placeholder="Initial password">
    </div>
  `;
}

/**
 * Collect create form values including provisioning fields
 */
function collectCreateForm(): CreateOrganizationRequest {
  const name = ($('#org-name').val() as string || '').trim();
  const description = ($('#org-description').val() as string || '').trim();
  const contactName = ($('#org-contact').val() as string || '').trim();
  const vhost = ($('#org-vhost').val() as string || '').trim();
  const networkPrefix = ($('#org-network-prefix').val() as string || '').trim();

  const req: CreateOrganizationRequest = {
    name,
    description: description || undefined,
    contact_name: contactName || undefined,
    vhost: vhost || undefined,
  };

  if (networkPrefix) {
    req.network_prefix = parseInt(networkPrefix, 10);
  }

  // Initial user fields — only include if username is provided
  const username = ($('#org-user-username').val() as string || '').trim();
  if (username) {
    const email = ($('#org-user-email').val() as string || '').trim();
    const password = ($('#org-user-password').val() as string || '').trim();

    req.initial_user = {
      username,
      email: email || undefined,
      password: password || undefined,
    };
  }

  return req;
}

/**
 * Collect modify form values (basic fields only)
 */
function collectOrgForm(): { name: string } & Partial<Organization> {
  const name = ($('#org-name').val() as string || '').trim();
  const description = ($('#org-description').val() as string || '').trim();
  const contactName = ($('#org-contact').val() as string || '').trim();
  const vhost = ($('#org-vhost').val() as string || '').trim();

  return {
    name,
    description: description || undefined,
    contact_name: contactName || undefined,
    vhost: vhost || undefined,
  };
}

/**
 * Create a new organization
 */
export async function createOrganization(): Promise<void> {
  const form = buildCreateForm();

  let saving = false;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Add Organization</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Add Organization',
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      buttons: {
        Create: async function () {
          if (saving) return;
          saving = true;

          const data = collectCreateForm();
          if (!data.name) {
            alert('Please enter an organization name');
            saving = false;
            return;
          }

          const parent = $(this);
          declareLoading();

          const result = await apiCall(api.createOrganization(data), 'Failed to Create Organization');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }

          declareLoaded();
          parent.dialog('close');
          fillOrganizationsTable();
          if (result.warnings?.length) {
            notificationDialog(result.warnings.map(escapeHtml).join('<br>'), true);
          }
          showAdSyncWarning(result);
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Modify an existing organization
 */
export async function modifyOrganization(name: string): Promise<void> {
  declareLoading();

  const org = await apiCall(api.getOrganization(name), 'Failed to Load Organization');
  if (!org) {
    declareLoaded();
    return;
  }

  const form = buildOrgForm(org);
  declareLoaded();

  let saving = false;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Modify Organization</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Modify Organization: ' + escapeHtml(name),
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      buttons: {
        Save: async function () {
          if (saving) return;
          saving = true;

          const data = collectOrgForm();
          const parent = $(this);
          declareLoading();

          const result = await apiCall(api.updateOrganization(name, data), 'Failed to Update Organization');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }

          declareLoaded();
          parent.dialog('close');
          fillOrganizationsTable();
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Delete an organization
 */
export async function deleteOrganization(name: string): Promise<void> {
  confirmDialog(
    `Are you sure you want to delete organization '${escapeHtml(name)}'?`,
    async () => {
      declareLoading();
      await apiCall(api.deleteOrganization(name), 'Failed to Delete Organization');
      await fillOrganizationsTable();
      declareLoaded();
    }
  );
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillOrganizationsTable = fillOrganizationsTable;
  (window as unknown as Record<string, unknown>).createOrganization = createOrganization;
  (window as unknown as Record<string, unknown>).modifyOrganization = modifyOrganization;
  (window as unknown as Record<string, unknown>).deleteOrganization = deleteOrganization;
}
